<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, nextTick } from 'vue';

const emit = defineEmits<{
  scroll: [position: number];
}>();

const containerRef = ref<HTMLDivElement | null>(null);
const contentRef = ref<HTMLDivElement | null>(null);
const lastScrollbarVisible = ref<boolean | null>(null);

let resizeObserver: ResizeObserver | null = null;

const onScroll = () => {
  const el = containerRef.value;
  if (!el) return;
  emit('scroll', el.scrollTop);
};

const checkScrollbar = () => {
  const el = containerRef.value;
  if (!el) return;
  const visible = el.scrollHeight > el.clientHeight;
  if (
    lastScrollbarVisible.value === null ||
    visible !== lastScrollbarVisible.value
  ) {
    lastScrollbarVisible.value = visible;
    // Instead of a separate event, emit a scroll position update:
    // - When the scrollbar appears, emit the current scrollTop
    // - When the scrollbar disappears, emit 0
    if (visible) {
      emit('scroll', el.scrollTop);
    } else {
      emit('scroll', 0);
    }
  }
};

const getScrollTop = (): number => containerRef.value?.scrollTop ?? 0;

const setScrollTop = (position: number): void => {
  if (containerRef.value) {
    containerRef.value.scrollTop = position;
  }
};

defineExpose({
  getScrollTop,
  setScrollTop,
});

onMounted(async () => {
  await nextTick();
  const el = containerRef.value;
  if (!el) return;

  // Scroll listener (passive for performance)
  el.addEventListener('scroll', onScroll, { passive: true });

  // Observe size/content changes to detect scrollbar appearance
  try {
    resizeObserver = new ResizeObserver(() => {
      checkScrollbar();
    });
    resizeObserver.observe(el);
    if (contentRef.value) resizeObserver.observe(contentRef.value);
  } catch (_) {
    // Fallback: listen to window resize if ResizeObserver is not available
    window.addEventListener('resize', checkScrollbar);
  }

  // Initial check
  checkScrollbar();
});

onBeforeUnmount(() => {
  const el = containerRef.value;
  if (el) el.removeEventListener('scroll', onScroll as EventListener);

  if (resizeObserver) {
    try {
      resizeObserver.disconnect();
    } catch {}
    resizeObserver = null;
  } else {
    window.removeEventListener('resize', checkScrollbar);
  }
});
</script>

<template>
  <div ref="containerRef">
    <div ref="contentRef" class="w-full">
      <slot />
    </div>
  </div>
</template>
